import { useState, useEffect } from "react";
import { Input, Select, Radio, theme } from "antd";
import {
    EditOutlined,
    CheckOutlined,
    CloseOutlined,
    PhoneOutlined,
} from "@ant-design/icons";
import { PhoneOff } from 'lucide-react';
import { displayName } from "react-quill";

type FieldType = "input" | "select" | "multi-select" | "radio";

interface Option {
    label: string;
    value: string;
}

interface Props {
    label?: string;
    type?: FieldType;
    value?: any;
    options?: Option[];
    onChange?: (val: any) => void;
}

export default function EditableField({
    label,
    type = "input",
    value,
    options = [],
    onChange,
}: Props) {
    const { token } = theme.useToken();
    const [editing, setEditing] = useState(false);
    const [tempValue, setTempValue] = useState(value);

    // Softphone State
    const [agentStatus, setAgentStatus] = useState({ state: 'ONLINE', colorClass: 'green' });
    const [isSoftphoneOpen, setIsSoftphoneOpen] = useState(false);
    const [dialerInput, setDialerInput] = useState('');
    const [callState, setCallState] = useState('idle'); // 'idle', 'calling', 'connected'
    const [callingNumber, setCallingNumber] = useState('');
    const [callingName, setCallingName] = useState('');
    const [callDuration, setCallDuration] = useState(0);
    const [currentTicketContext, setCurrentTicketContext] = useState(null);
    const [callLogs, setCallLogs] = useState([]);

    const Icons = {
        Ticket: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"></path></svg>,
        Users: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>,
        Monitor: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"></path></svg>,
        Cog: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>,
        Search: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>,
        Bell: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path></svg>,
        Phone: () => <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>,
        Sitemap: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM9 18a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2zM9 10v2a2 2 0 002 2h2a2 2 0 002-2v-2M10 14h4"></path></svg>,
        ChevronDown: () => <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>,
        Inbox: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path></svg>,
        Clock: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>,
        Star: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"></path></svg>,
        Plus: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>,
        ArrowUp: () => <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path></svg>,
        ArrowDown: () => <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path></svg>,
        PhoneMissed: () => <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3l18 18M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>,
        PhoneOff: () => <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path></svg>,
        UserSecret: () => <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4"></path></svg>,
        Play: () => <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>,
        ChatDots: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path></svg>,
        ChevronLeft: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>,
        ChevronRight: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>,
        Pen: () => <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>,
        Calendar: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>,
        Comments: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"></path></svg>,
        Sync: () => <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>,
        Image: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>,
        FolderOpen: () => <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z"></path></svg>,
        ExclamationTriangle: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>,
        QuestionCircle: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>,
        Headset: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 11.696V10a8 8 0 10-16 0v1.696A4.001 4.001 0 003 15v3a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2v-1.696A5.98 5.98 0 0112 6a5.98 5.98 0 017 4.304V12a2 2 0 00-2 2v3a2 2 0 002 2h1a2 2 0 002-2v-3a4.001 4.001 0 00-1-3.304z"></path></svg>,
        CheckCircle: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>,
        PauseCircle: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>,
        TimesCircle: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>,
        Times: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>,
        Backspace: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2 2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z"></path></svg>
    };

    useEffect(() => {
        setTempValue(value);
    }, [value]);

    const handleSave = () => {
        onChange?.(tempValue);
        setEditing(false);
    };

    const handleCancel = () => {
        setTempValue(value);
        setEditing(false);
    };

    const renderField = () => {
        switch (type) {
            case "select":
                return (
                    <Select
                        value={tempValue}
                        onChange={setTempValue}
                        options={options}
                        className="w-[200px]"
                        size="small"
                    />
                );

            case "multi-select":
                return (
                    <Select
                        mode="multiple"
                        value={tempValue}
                        onChange={setTempValue}
                        options={options}
                        className="w-[250px]"
                        size="small"
                    />
                );

            case "radio":
                return (
                    <Radio.Group
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                    >
                        {options.map((opt) => (
                            <Radio key={opt.value} value={opt.value}>
                                {opt.label}
                            </Radio>
                        ))}
                    </Radio.Group>
                );

            default:
                return (
                    <Input
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        size="small"
                        className="w-[220px]"
                        onPressEnter={handleSave}
                    />
                );
        }
    };

    const handleMakeCall = (phone, name, ticketContext = null) => {
        setCurrentTicketContext(ticketContext);
        setIsSoftphoneOpen(true);
        setDialerInput(phone);
        handleStartCall(phone, name);
    };

    const handleStartCall = (phone, name) => {
        if (!phone) return;
        setCallingNumber(phone);
        setCallingName(name || 'Khách hàng');
        setCallState('calling');

        // Simulate connecting delay
        setTimeout(() => {
            setCallState('connected');
        }, 2000);
    };

    const handleEndCall = () => {
        setCallState('ended');

        const timeStr = new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
        const formatDuration = (seconds) => {
            const m = Math.floor(seconds / 60).toString().padStart(2, '0');
            const s = (seconds % 60).toString().padStart(2, '0');
            return `${m}:${s}`;
        };

        const newLog = {
            id: Date.now(),
            name: callingName,
            time: timeStr,
            duration: formatDuration(callDuration),
            ticketContext: currentTicketContext
        };

        setCallLogs(prev => [newLog, ...prev]);

        setTimeout(() => {
            setCallState('idle');
            setDialerInput('');
            setCurrentTicketContext(null);
        }, 1000);
    };

    const formatDurationDisplay = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    return (
        <div className="flex justify-between items-start">
            {label && (
                <div
                    className="w-40 text-sm"
                    style={{ color: token.colorTextSecondary }}
                >
                    {label}
                </div>
            )}

            <div className="flex-1 flex items-center gap-2">
                {!editing ? (
                    <>
                        <div className="text-[18px] font-bold">
                            {value && value.length !== 0 ? (
                                Array.isArray(value)
                                    ? value.join(", ")
                                    : value
                            ) : (
                                <span style={{ color: token.colorTextTertiary }}>
                                    —
                                </span>
                            )}
                        </div>

                        <EditOutlined
                            style={{ color: token.colorPrimary }}
                            className="cursor-pointer text-[18px]"
                            onClick={() => setEditing(true)}
                        />

                        <button onClick={() => handleMakeCall('0909866868', 'Mr.Boss')} className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm font-medium flex items-center gap-1 transition-colors ml-2">
                            <PhoneOutlined rotate={90} style={{ fontSize: '18px', color: '#fff' }} /> Gọi điện
                        </button>
                    </>
                ) : (
                    <>
                        {renderField()}

                        <CheckOutlined
                            className="cursor-pointer"
                            style={{ color: token.colorSuccess }}
                            onClick={handleSave}
                        />

                        <CloseOutlined
                            className="cursor-pointer"
                            style={{ color: token.colorError }}
                            onClick={handleCancel}
                        />
                    </>
                )}
            </div>

            <div className={`fixed bottom-6 right-6 w-72 bg-white rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.3)] border border-gray-200 z-50 transition-transform duration-300 transform flex flex-col overflow-hidden ${isSoftphoneOpen ? 'translate-y-0' : 'translate-y-[120%]'}`}>
                <div className="bg-gray-900 text-white p-3 flex justify-between items-center cursor-move">
                    <div className="flex items-center gap-2">
                        <span className="text-blue-400"><Icons.Headset /></span>
                        <span className="font-semibold text-sm">MiCall Extension</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full bg-${agentStatus.colorClass === 'gray' ? 'red' : 'green'}-500`}></div>
                        <button onClick={() => setIsSoftphoneOpen(false)} className="text-gray-400 hover:text-white"><Icons.Times /></button>
                    </div>
                </div>

                {callState === 'idle' ? (
                    <div className="p-4 flex flex-col items-center">
                        <input type="text" value={dialerInput} onChange={(e) => setDialerInput(e.target.value)} placeholder="Nhập số điện thoại..." className="w-full text-center text-lg font-bold text-gray-700 border-b-2 border-gray-200 focus:border-blue-500 focus:outline-none pb-2 mb-4 bg-transparent" />

                        <div className="grid grid-cols-3 gap-3 w-full px-2 mb-4">
                            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0'].map(num => (
                                <button key={num} onClick={() => setDialerInput(prev => prev + num)} className="h-10 rounded bg-gray-100 hover:bg-gray-200 font-semibold text-gray-700">{num}</button>
                            ))}
                            <button onClick={() => setDialerInput(prev => prev.slice(0, -1))} className="h-10 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center"><Icons.Backspace /></button>
                        </div>
                        <button onClick={() => handleStartCall(dialerInput, 'Không xác định')} className="w-12 h-12 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center text-xl shadow transition transform hover:scale-105">
                            <Icons.Phone />
                        </button>
                    </div>
                ) : (
                    <div className="p-6 flex flex-col items-center bg-gray-900 text-white relative">
                        {callState === 'calling' && (
                            <div className="absolute inset-0 opacity-20 pointer-events-none flex items-center justify-center">
                                <div className="w-32 h-32 rounded-full border-2 border-green-500 pulse-ring"></div>
                            </div>
                        )}
                        <h4 className="text-gray-400 text-xs mb-1 z-10">
                            {callState === 'calling' ? 'Đang kết nối...' : callState === 'connected' ? 'Đang đàm thoại' : 'Đã kết thúc'}
                        </h4>
                        <h2 className="text-xl font-bold mb-1 z-10">{callingNumber}</h2>
                        <p className="text-xs text-gray-300 mb-4 z-10">{callingName}</p>
                        <div className="w-32 h-32 rounded-full border-2 border-green-500 pulse-ring flex items-center justify-center mb-4 relative">
                            <div className={`text-2xl font-mono z-10 ${callState === 'connected' ? 'text-green-400' : 'text-gray-500'}`}>
                                {formatDurationDisplay(callDuration)}
                            </div>
                        </div>
                        <button onClick={handleEndCall} disabled={callState === 'ended'} className={`w-12 h-12 rounded-full flex items-center justify-center text-xl shadow transition z-10 ${callState === 'ended' ? 'bg-gray-600 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600 hover:scale-105'}`}>
                            <PhoneOff size={20} color="#fff" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}